// Templates de stories: lista dos proprios, padrao geral/por loja, upload do
// fundo recortado e geracao pelo bot (fila story_render_requests, mesmo motor
// da marca d'agua) com download do JPEG.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StorySettings } from "@lucralink/shared";
import { supabase } from "@/lib/supabase";
import { STORY_BUCKET, storyErrorMessage, templatePathFor } from "@/lib/stories";

export interface CustomStoryTemplate {
  id: string;
  name: string;
  path: string;
  previewUrl: string | null;
}

const TEMPLATES_KEY = ["story-templates"] as const;
const SETTINGS_KEY = ["story-settings"] as const;
export const RENDER_POLL_MS = 700;
export const RENDER_TIMEOUT_MS = 30_000;

async function signed(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(STORY_BUCKET).createSignedUrl(path, 60 * 30);
  return error ? null : (data?.signedUrl ?? null);
}

export function useStoryTemplates() {
  return useQuery<CustomStoryTemplate[], Error>({
    queryKey: TEMPLATES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from("story_templates").select("id, name, storage_path").order("created_at", { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as Array<{ id: string; name: string; storage_path: string }>;
      return Promise.all(rows.map(async (r) => ({ id: r.id, name: r.name, path: r.storage_path, previewUrl: await signed(r.storage_path) })));
    },
    staleTime: 60_000,
  });
}

export function useStorySettings() {
  return useQuery<StorySettings, Error>({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from("story_settings").select("default_ref, by_provider").limit(1).maybeSingle();
      if (error) throw error;
      const row = data as { default_ref: string | null; by_provider: Record<string, string> | null } | null;
      return { defaultRef: row?.default_ref ?? null, byProvider: row?.by_provider ?? {} };
    },
    staleTime: 60_000,
  });
}

export function useSaveStorySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: StorySettings) => {
      const { error } = await supabase.rpc("save_story_settings", { p_default_ref: settings.defaultRef, p_by_provider: settings.byProvider });
      if (error) throw new Error(storyErrorMessage(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

/** Sobe o fundo ja recortado (JPEG 1080x1920) e registra o template. */
export function useUploadStoryTemplate(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, blob }: { name: string; blob: Blob }) => {
      if (!tenantId) throw new Error("Empresa não carregada.");
      const path = templatePathFor(tenantId, crypto.randomUUID());
      const upload = await supabase.storage.from(STORY_BUCKET).upload(path, blob, { contentType: "image/jpeg", upsert: false });
      if (upload.error) throw new Error("Não foi possível enviar a imagem.");
      const { error } = await supabase.rpc("create_story_template", { p_name: name, p_path: path });
      if (error) {
        // Sem registro, o arquivo seria lixo no bucket.
        await supabase.storage.from(STORY_BUCKET).remove([path]);
        throw new Error(storyErrorMessage(error));
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

/** Apaga o template; se era padrao, o padrao volta ao modelo pronto (RPC). */
export function useDeleteStoryTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string | null> => {
      const { data, error } = await supabase.rpc("delete_story_template", { p_id: id });
      if (error) throw new Error(storyErrorMessage(error));
      const removed = await supabase.storage.from(STORY_BUCKET).remove([String(data)]);
      return removed.error ? "Template apagado, mas o arquivo não pôde ser removido do armazenamento." : null;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
      await qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function waitForRender(id: string): Promise<string> {
  const deadline = Date.now() + RENDER_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const { data, error } = await supabase.from("story_render_requests").select("status, storage_path, error").eq("id", id).maybeSingle();
    if (error) throw new Error("Não foi possível acompanhar a geração do story.");
    const row = data as { status: string; storage_path: string | null } | null;
    if (row?.status === "done" && row.storage_path) {
      const url = await signed(row.storage_path);
      if (!url) throw new Error("Não foi possível abrir o story gerado.");
      return url;
    }
    if (row?.status === "failed") throw new Error("A geração do story falhou. Tente outro template ou outro link.");
    await sleep(RENDER_POLL_MS);
  }
  throw new Error("O gerador de stories não respondeu. Tente de novo em instantes.");
}

/** Pede o story do link (template escolhido ou o padrao) e devolve a URL do JPEG. */
export function useRenderStory() {
  return useMutation({
    mutationFn: async ({ linkId, templateRef }: { linkId: string; templateRef: string | null }): Promise<string> => {
      const { data, error } = await supabase.rpc("request_story_render", { p_link_id: linkId, p_template_ref: templateRef });
      if (error) throw new Error(storyErrorMessage(error));
      return waitForRender(String(data));
    },
  });
}
